/**
 * ADR-168 — the pure rules behind correcting a CLOSED employment contract on
 * the Employment Episodes tab, kept out of the component so they can be tested
 * in the node jest environment. The API enforces every one of them too; these
 * only decide what the screen offers.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/** Open = still "Present" (no end date). */
export const isActiveEpisode = (episode) => !episode?.endDate;

/**
 * Does this card get an edit pencil?
 *   open   → ADR-019 / ADR-136 A5: full right, or the annotation right on an
 *            episode the caller may annotate
 *   closed → ADR-168 D8: the full right AND the history-correction right
 *            (annotation-only holders stay on open contracts — D7)
 */
export const canEditEpisode = (episode, { canModify, canAnnotate, canCorrectHistory, isAnnotatable }) => {
  if (isActiveEpisode(episode)) {
    return Boolean(canModify || (canAnnotate && isAnnotatable));
  }
  return Boolean(canModify && canCorrectHistory);
};

/** Does this card's event table get the edit column? Same rule, full right only. */
export const canEditEventsOf = (episode, { canModify, canCorrectHistory }) =>
  Boolean(canModify && (isActiveEpisode(episode) || canCorrectHistory));

/**
 * Client-side checks for the closed-contract modal, mirroring G1/G2/G4.
 * @param {Object} form - { startDate, endDate } as dayjs values (or null)
 * @param {string} [today] - 'YYYY-MM-DD', injectable for tests
 * @returns {string|null} the message to show, or null when the form is fine
 */
export const validateClosedEpisodeForm = (form, today = dayjs.utc().format('YYYY-MM-DD')) => {
  if (!form?.startDate || !form.startDate.isValid()) {
    return 'A valid start date is required.';
  }
  if (!form.endDate || !form.endDate.isValid()) {
    return 'A closed contract keeps its end date: it can be moved, not cleared. Reopening a past contract is a rehire.';
  }
  if (form.endDate.format('YYYY-MM-DD') > today) {
    return 'The end date of a closed contract cannot be after today.';
  }
  if (form.startDate.isAfter(form.endDate, 'day')) {
    return 'Start date must be on or before end date.';
  }
  return null;
};

/** A dry-run result asks for a confirm step when it carries any warning. */
export const needsConfirmation = (dryRunResult) =>
  Array.isArray(dryRunResult?.warnings) && dryRunResult.warnings.length > 0;

/** One line per warning for the confirm step, server message first. */
export const warningLines = (warnings) => (warnings || []).map((w) => w.message || w.code);
